import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronRight, Building2, MapPin, FileText } from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import SearchInput from 'src/components/common/SearchInput';
import TablePagination from 'src/components/common/TablePagination';
import { Button } from 'src/components/ui/button';
import { formatDate } from 'src/lib/utils';
import { JobService } from 'src/services/api/JobService';
import JobPostingsModal from './JobPostingsModal';

// GET /internal/jobs carries no status field at all today, so there's no
// Active/Draft/Completed to show (an earlier pass here hardcoded a pill to
// "Open" for every card, which is worse — this drops it rather than lying).
// Add it back once the backend actually tracks a status.
const normalizeJobPosting = (job, index) => ({
  id: String(job?.id || job?.job_id || job?.jobId || `JOB-${index + 1}`),
  code: job?.job_code || job?.jobCode || null,
  title: job?.title || job?.job_title || job?.name || 'Untitled Job',
  company: job?.organization_name || 'Ahedge',
  locationCount: job?.locations?.length ?? 0,
  postedOn:
    job?.postedOn || job?.posted_on || job?.createdAt || job?.created_at,
  closesOn: job?.deadline || job?.closes_on || job?.closesOn,
  applicants: job?.applicants ?? job?.applicant_count ?? job?.stats?.applicants,
});

const extractJobs = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.jobs)) return payload.jobs;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
};

// Candidate-pipeline funnel strip, mirroring the reference ATS card's
// Application/Shortlist/Interview/Offer/Hired/Rejected row. Only
// "Application" has a real number behind it (applicant_count, from the
// live GET /internal/jobs response) — the rest have no backing field
// anywhere in this API today, so they render as an explicit 0 rather than
// this card silently pretending the funnel doesn't exist. Swap each `value`
// for its real field the moment the backend starts returning one.
const FUNNEL_STAGES = [
  { key: 'application', label: 'Application', Icon: FileText },
];

const FunnelCell = ({ label, Icon, value }) => (
  <div className="flex items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-2.5 py-[7px] text-[11.5px] flex-1 max-w-[130px]">
    <Icon
      className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0"
      strokeWidth={1.8}
    />
    <span className="text-foreground-muted truncate">{label}</span>
    <span className="w-px h-3 bg-border flex-shrink-0" aria-hidden="true" />
    <span className="font-mono font-semibold text-foreground-strong ml-auto">
      {value}
    </span>
  </div>
);

// Row-style card — title, a fact row (real job_code/company/locations/
// closing date), and the funnel strip below, whole card clickable through
// to the detail page plus an explicit "Detail" button matching the
// reference. No status pill/accent bar: see normalizeJobPosting's comment
// on why there's no status field to color it by.
const JobPostingCard = ({ posting, navigate }) => {
  const handleClick = () =>
    navigate(`/job-postings/${posting.id}`, { state: { posting } });
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') handleClick();
  };

  const funnelValues = {
    application: posting.applicants ?? 0,
    shortlist: 0,
    interview: 0,
    offer: 0,
    hired: 0,
    rejected: 0,
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="bg-card border border-border rounded-[10px] px-5 py-4 cursor-pointer transition-shadow hover:shadow-[0_4px_14px_rgba(0,0,0,.06)]"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[15px] font-[650] text-foreground-strong tracking-[-0.01em] leading-snug">
            {posting.title}
          </h3>

          <div className="flex items-center gap-[7px] mt-[5px] text-[11.5px] flex-wrap text-foreground-muted">
            {posting.code && (
              <>
                <span className="font-mono font-semibold">{posting.code}</span>
                <span
                  className="w-[3px] h-[3px] rounded-full bg-border"
                  aria-hidden="true"
                />
              </>
            )}
            <span className="flex items-center gap-[5px] font-medium">
              <Building2 className="w-3.5 h-3.5" strokeWidth={1.8} />
              {posting.company}
            </span>
            <span
              className="w-[3px] h-[3px] rounded-full bg-border"
              aria-hidden="true"
            />
            <span className="flex items-center gap-[5px]">
              <MapPin className="w-3.5 h-3.5" strokeWidth={1.8} />
              {posting.locationCount}{' '}
              {posting.locationCount === 1 ? 'location' : 'locations'}
            </span>
            {posting.closesOn && (
              <>
                <span
                  className="w-[3px] h-[3px] rounded-full bg-border"
                  aria-hidden="true"
                />
                <span>Closes {formatDate(posting.closesOn)}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <span className="text-[11px] text-foreground-muted whitespace-nowrap">
            Posted {formatDate(posting.postedOn)}
          </span>
          <Button
            variant="outline"
            size="headerSm"
            className="gap-1"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            Detail
            <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-3">
        {FUNNEL_STAGES.map(({ key, label, Icon }) => (
          <FunnelCell
            key={key}
            label={label}
            Icon={Icon}
            value={funnelValues[key]}
          />
        ))}
      </div>
    </div>
  );
};

// Mirrors JobPostingCard's own shape (title/meta row + funnel strip) so the
// loading state reads as "the same kind of list," not a bespoke spinner box.
const JobPostingListSkeleton = () => (
  <div className="flex flex-col gap-[10px]">
    {['sk-0', 'sk-1', 'sk-2', 'sk-3', 'sk-4'].map((id) => (
      <div
        key={id}
        className="bg-card border border-border rounded-[10px] px-5 py-4 animate-pulse"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 space-y-[8px]">
            <div className="h-[18px] bg-muted rounded-[4px] w-2/5" />
            <div className="h-[13px] bg-muted rounded-[4px] w-3/5" />
          </div>
          <div className="h-[30px] bg-muted rounded-lg w-[86px] flex-shrink-0" />
        </div>
        <div className="flex gap-2 mt-3">
          {FUNNEL_STAGES.map(({ key }) => (
            <div
              key={key}
              className="h-[30px] bg-muted rounded-lg flex-1 min-w-[104px]"
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const JobPostingsEmptyState = ({ searchQuery, onCreate }) => (
  <div className="border border-border rounded-xl bg-card">
    <div className="flex flex-col items-center justify-center py-[50px] px-5 text-center">
      <div className="w-[46px] h-[46px] rounded-xl bg-brand-bg-subtle text-primary inline-flex items-center justify-center mb-3">
        <Building2 className="w-[22px] h-[22px]" strokeWidth={1.8} />
      </div>
      <p className="text-[15px] font-[650] text-foreground-strong mb-1">
        No job postings found
      </p>
      <p className="text-[13px] text-foreground-muted max-w-[320px] mb-5">
        {searchQuery
          ? 'No job postings match your search.'
          : 'Job postings will appear here once they are created.'}
      </p>
      <Button
        className="bg-primary hover:bg-brand-deep text-white text-[13px] font-semibold rounded-lg h-9 px-4 border-0"
        onClick={onCreate}
      >
        <Plus className="h-4 w-4" />
        Create Job Post
      </Button>
    </div>
  </div>
);

const JobPostings = () => {
  const navigate = useNavigate();
  const [postings, setPostings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isOpen, setIsOpen] = useState(false);
  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  const fetchJobs = useCallback(async () => {
    setIsLoading(true);
    setListError('');
    try {
      const response = await JobService.listJobs();
      const normalizedPostings = extractJobs(response).map(normalizeJobPosting);
      setPostings(normalizedPostings);
    } catch (error) {
      console.error('Failed to fetch job postings:', error);
      setPostings([]);
      setListError('Unable to load job postings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handlePublish = () => {
    closeModal();
    fetchJobs();
  };

  const handleSaveDraft = () => {
    closeModal();
    fetchJobs();
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setCurrentPage(1);
  };

  const filteredPostings = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return postings;

    return postings.filter((posting) => {
      const titleMatch = String(posting.title || '')
        .toLowerCase()
        .includes(query);
      const idMatch = String(posting.id || '')
        .toLowerCase()
        .includes(query);
      const companyMatch = String(posting.company || '')
        .toLowerCase()
        .includes(query);
      const locationMatch = String(posting.location || '')
        .toLowerCase()
        .includes(query);
      return titleMatch || idMatch || companyMatch || locationMatch;
    });
  }, [postings, searchQuery]);

  const totalItems = filteredPostings.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);

  const pagedPostings = filteredPostings.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize
  );

  return (
    <div>
      <PageHeader
        title="Job Postings"
        actions={
          <Button
            variant="outline"
            size="headerSm"
            className="gap-1.5"
            onClick={openModal}
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
            Create Job Post
          </Button>
        }
      />

      <PageContainer className="space-y-4">
        <SearchInput
          value={searchQuery}
          onValueChange={handleSearchChange}
          placeholder="Search by title, ID or company"
          className="max-w-xl"
        />

        {listError ? (
          <div className="rounded-xl border border-ds-danger/20 bg-ds-danger-subtle p-5">
            <div className="text-[13px] font-semibold text-ds-danger">
              {listError}
            </div>
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={fetchJobs}>
                Retry
              </Button>
            </div>
          </div>
        ) : null}

        {isLoading ? <JobPostingListSkeleton /> : null}

        {!isLoading && !listError && pagedPostings.length > 0 && (
          <div className="flex flex-col gap-[10px]">
            {pagedPostings.map((posting) => (
              <JobPostingCard
                key={posting.id}
                posting={posting}
                navigate={navigate}
              />
            ))}
          </div>
        )}

        {!isLoading && !listError && pagedPostings.length === 0 ? (
          <JobPostingsEmptyState
            searchQuery={searchQuery}
            onCreate={openModal}
          />
        ) : null}

        {!isLoading && !listError && totalItems > 0 && (
          <TablePagination
            currentPage={safePage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="job postings"
          />
        )}
      </PageContainer>

      <JobPostingsModal
        open={isOpen}
        onClose={closeModal}
        requirements={[]}
        onPublish={handlePublish}
        onSaveDraft={handleSaveDraft}
      />
    </div>
  );
};

export default JobPostings;
